import dayjs from 'dayjs';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { DateTime } from 'src_common/utils/time/time';

const cleanFloatNumberString = (s: string): string => s.replaceAll(',', '.');
const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

/** @description Allows positive numbers with a maximum of two decimal places */
export const parseNumeric = (numeric: string): number => {
    const cleanNumeric = cleanFloatNumberString(numeric);
    if (/^\d+(\.\d{1,2})?$/.test(cleanNumeric.trim())) {
        return parseFloat(cleanNumeric);
    }
    return NaN;
};

export const isStakeNumeric =
    (optional: boolean, message: string) =>
    (value: string): Result<string> => {
        if (optional === true && value === '') {
            return Result.createOk(value);
        }
        const numValue = parseNumeric(value);
        if (isNaN(numValue)) {
            return Result.createError(message);
        }
        return Result.createOk(value);
    };

export const isStakeGreaterOrEqual =
    (getFromAmount: () => string, message: string) =>
    (value: string): Result<string> => {
        if (value === '' || getFromAmount() === '') {
            return Result.createOk(value);
        } else {
            const toAmount = parseNumeric(value);
            const threshold = parseNumeric(getFromAmount());
            if (toAmount >= threshold) {
                return Result.createOk(value);
            } else {
                return Result.createError(message);
            }
        }
    };

export const isDateValid =
    (message: string) =>
    (value: string): Result<string> => {
        const isValid = dayjs(value, dateFormat, true).isValid();
        return isValid ? Result.createOk(value) : Result.createError(message);
    };

export const isDateAfter =
    (getFromDate: () => string, inclusive: boolean, message: string) =>
    (value: string): Result<string> => {
        const toDate = DateTime.from(value);
        const threshold = getFromDate();
        const fromDate = DateTime.from(threshold);
        if (fromDate === null || toDate === null) {
            return Result.createError(message);
        }
        if (inclusive && toDate.isSame(fromDate)) {
            return Result.createOk(value);
        }
        return toDate.isAfter(fromDate) ? Result.createOk(value) : Result.createError(message);
    };

export const isDateBefore =
    (getFromDate: () => string, inclusive: boolean, message: string) =>
    (value: string): Result<string> => {
        const toDate = DateTime.from(value);
        const threshold = getFromDate();
        const fromDate = DateTime.from(threshold);
        if (fromDate === null || toDate === null) {
            return Result.createError(message);
        }
        if (inclusive && fromDate.isSame(toDate)) {
            return Result.createOk(value);
        }
        return fromDate.isAfter(toDate) ? Result.createOk(value) : Result.createError(message);
    };
